<script setup lang="ts">
// 描画色 / 背景色のカラーピッカー (HSV 正方形 + 色相スライダー + HEX / RGB 入力)。
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useUiStore } from '@/stores/uiStore'
import { useI18n } from '@/i18n'
import { hexToRgb, rgbToHex, rgbToHsv, hsvToRgb, type HSV } from '@/color/convert'

const props = defineProps<{ target: 'fg' | 'bg' }>()
const emit = defineEmits<{ close: [] }>()
const ui = useUiStore()
const { t } = useI18n()

const current = computed({
  get: () => (props.target === 'fg' ? ui.foreground : ui.background),
  set: (v: string) => {
    if (props.target === 'fg') ui.foreground = v
    else ui.background = v
  }
})
const hsv = ref<HSV>(rgbToHsv(hexToRgb(current.value) ?? { r: 0, g: 0, b: 0 }))
const hexInput = ref(current.value)
watch(current, v => {
  hexInput.value = v
  const rgb = hexToRgb(v)
  if (rgb) {
    const h = rgbToHsv(rgb)
    // 彩度 0 / 明度 0 のとき色相を保つ
    if (h.s > 0 && h.v > 0) hsv.value.h = h.h
    hsv.value.s = h.s
    hsv.value.v = h.v
  }
})
const rgb = computed(() => hexToRgb(current.value) ?? { r: 0, g: 0, b: 0 })
const hueColor = computed(() => rgbToHex(hsvToRgb({ h: hsv.value.h, s: 100, v: 100 })))

function commitHsv() {
  current.value = rgbToHex(hsvToRgb(hsv.value))
}
function setRgb(k: 'r' | 'g' | 'b', v: number) {
  if (!Number.isFinite(v)) return
  current.value = rgbToHex({ ...rgb.value, [k]: Math.max(0, Math.min(255, Math.round(v))) })
}
function commitHex() {
  const p = hexToRgb(hexInput.value)
  if (p) current.value = rgbToHex(p)
  else hexInput.value = current.value
}

const square = ref<HTMLDivElement | null>(null)
function pickSquare(e: PointerEvent) {
  const r = square.value!.getBoundingClientRect()
  hsv.value.s = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100))
  hsv.value.v = Math.max(0, Math.min(100, 100 - ((e.clientY - r.top) / r.height) * 100))
  commitHsv()
}
function onSquareDown(e: PointerEvent) {
  ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  pickSquare(e)
}
function onSquareMove(e: PointerEvent) {
  if (e.buttons & 1) pickSquare(e)
}
function onHue(e: Event) {
  hsv.value.h = Number((e.target as HTMLInputElement).value)
  commitHsv()
}
function onDocDown(e: PointerEvent) {
  if (!(e.target instanceof HTMLElement) || !e.target.closest('.picker, .colors')) emit('close')
}
onMounted(() => document.addEventListener('pointerdown', onDocDown))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocDown))
</script>

<template>
  <div class="picker" @keydown.esc="emit('close')">
    <div class="head row"><span>{{ t(target === 'fg' ? 'color.foreground' : 'color.background') }}</span><span class="spacer" /><button class="ghost icon" @click="emit('close')">×</button></div>
    <div ref="square" class="square" :style="{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})` }" @pointerdown="onSquareDown" @pointermove="onSquareMove">
      <div class="dot" :style="{ left: hsv.s + '%', top: 100 - hsv.v + '%' }" />
    </div>
    <input class="hue" type="range" min="0" max="360" :value="hsv.h" @input="onHue" />
    <div class="row">
      <span class="preview" :style="{ background: current }" />
      <label class="row"><span class="muted">{{ t('color.hex') }}</span><input v-model="hexInput" class="hex mono" @change="commitHex" @keydown.enter="commitHex" /></label>
    </div>
    <div class="row rgb">
      <label><span class="muted">R</span><input type="number" min="0" max="255" :value="rgb.r" @change="setRgb('r', Number(($event.target as HTMLInputElement).value))" /></label>
      <label><span class="muted">G</span><input type="number" min="0" max="255" :value="rgb.g" @change="setRgb('g', Number(($event.target as HTMLInputElement).value))" /></label>
      <label><span class="muted">B</span><input type="number" min="0" max="255" :value="rgb.b" @change="setRgb('b', Number(($event.target as HTMLInputElement).value))" /></label>
    </div>
  </div>
</template>

<style scoped>
.picker {
  position: absolute;
  left: 46px;
  bottom: 8px;
  width: 240px;
  background: var(--bg-2);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 60;
}
.head {
  font-weight: 600;
}
.square {
  position: relative;
  height: 150px;
  border-radius: var(--radius-sm);
  cursor: crosshair;
  touch-action: none;
}
.dot {
  position: absolute;
  width: 10px;
  height: 10px;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #000;
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.hue {
  width: 100%;
  height: 12px;
  -webkit-appearance: none;
  appearance: none;
  background: linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);
  border-radius: 6px;
}
.preview {
  width: 28px;
  height: 22px;
  border: 1px solid var(--line-strong);
  border-radius: 3px;
}
.hex {
  width: 80px;
}
.rgb label {
  display: flex;
  align-items: center;
  gap: 3px;
}
.rgb input {
  width: 52px;
}
</style>
